import axios from 'axios';
import dayjs from 'dayjs';
import Micromodal from 'micromodal';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Setting } from '../../App';
import { billingPolicyIntervals, CUSTOMER_API_URL } from '../../constants';
import { useSubscriptionDetailListItem } from '../../hooks/use-subscription-detail-list-item';
import SubscriptionListClass from '../../styles/SubscriptionList.module.css';
import { calculateChangeDeadline } from '../../utils/calculateChangeDeadline';

type AllowModifyDay = {
  title: string;
  day: number;
  limitDate?: dayjs.Dayjs;
};
type Plan = {
  planId: string;
  name: string;
  description: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  pricingPolicyAdjustmentType: string | null;
  pricingPolicyAdjustmentValue: number | null;
};

type Box = {
  boxId: string;
  title: string;
  description: string;
  parentProductId: string;
};
type PlanGroup = {
  merchantCode: string;
  name: string;
  planGroupId: string;
  plans: Plan[];
  boxes: Box[];
};

type PageStatus = {
  removeAction: boolean;
  newOrderAction: boolean;
  quantityAction: boolean;
  planModifyAction: boolean;
};

const ChangeDeliverDate: FC<{
  setting: Setting;
  subscriptionContractId: string;
  updateSubscription: () => void;
}> = ({ setting, subscriptionContractId, updateSubscription }) => {
  const [allowModifyDay, setAllowModifyDay] = useState<AllowModifyDay>({
    title: '',
    day: 3,
  });
  const [id, setId] = useState<string>('');
  const [modifyPlanId, setModifyPlanId] = useState<string>('');
  const [pageStatus, setPageStatus] = useState<PageStatus>({
    removeAction: false,
    newOrderAction: false,
    quantityAction: false,
    planModifyAction: false,
  });
  const [isProc, setIsProc] = useState<boolean>(false);
  const { subscription, setSubscription, deliveryDate, setDeliveryDate, planGroups } = useSubscriptionDetailListItem(
    setting,
    subscriptionContractId,
  );
  const [isMessageSent, setIsMessageSent] = useState(false);
  useEffect(() => {
    if (!subscription || subscription!.disabled) return;

    setId(subscription.subscriptionContractId);

    const limitDate = calculateChangeDeadline(subscription);
    setAllowModifyDay({
      ...allowModifyDay,
      day: subscription.shop.allowModifyDays,
      limitDate,
    });

    const nextBilling = dayjs(subscription.nextBillingDate);
    const purchaseAt = subscription!.billingAttempts.length
      ? subscription!.billingAttempts[0].createdAt
      : subscription!.createdAt;
    const nextDelivery = nextDeliveryDate(
      nextBilling,
      subscription!.deliveryDays === -1 ? subscription!.shop.deliveryDays : subscription!.deliveryDays,
    );
    const nextDeliveryLimit = nextDeliveryDate(nextDelivery, subscription!.shop!.deliveryDaysMax);
    const nextBillingLimit = nextBilling.add(
      subscription!.billingPolicyIntervalCount,
      subscription!.billingPolicyInterval.toLowerCase() as 'day' | 'week' | 'month',
    );

    let nextDeliveryMin: dayjs.Dayjs | undefined = nextBilling.add(subscription!.shop!.deliveryDays + 1, 'day');
    let nextDeliveryMax: dayjs.Dayjs | undefined = nextBilling.add(30, 'day');

    if (subscription!.shop!.changeNextBillingDateWithDeliveryDate) {
      nextDeliveryMax =
        subscription!.shop!.deliveryDaysMax === 0
          ? undefined
          : nextBillingLimit.isAfter(nextDeliveryLimit)
            ? nextDeliveryLimit
            : nextBillingLimit;
      nextDeliveryMin = dayjs()
        .add(subscription!.shop.deliveryDays, 'day')
        .isAfter(dayjs(purchaseAt).add(subscription!.shop!.deliveryDays, 'day'))
        ? dayjs().add(subscription!.shop.deliveryDays + 1, 'day')
        : dayjs(purchaseAt).add(subscription!.shop!.deliveryDays, 'day');
    }

    setDeliveryDate({
      current: nextDelivery,
      change: nextDelivery,
      min: nextDeliveryMin,
      max: nextDeliveryMax,
      time: subscription!.deliveryTime,
      noDay: subscription!.deliveryDays === -1 && subscription!.shop.enableDeliveryDateNoDay,
    });

    // MicroModal の初期化
    Micromodal.init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  useEffect(() => {
    window.removeEventListener('message', actionChangeDeliverdateAndPlans);
    if (subscription && planGroups && deliveryDate) {
      window.addEventListener('message', actionChangeDeliverdateAndPlans);
    }
  }, [subscription, planGroups]);

  const lastEventData = useRef(null);

  const actionChangeDeliverdateAndPlans = useCallback(
    async (e: MessageEvent<any>) => {
      if (lastEventData.current && JSON.stringify(lastEventData.current) === JSON.stringify(e.data)) {
        // データが同じであれば処理をスキップ
        return;
      }

      // 変更を保存ボタンが押されたとき
      if (e.data.action && e.data.action === 'changeDeliverdateAndPlans') {
        if (isProc) {
          return;
        }
        // console.log('interval',e.data.deliverinterval);
        // console.log('deliverdate',e.data.deliverdate)
        // console.log('delivertime',e.data.delivertime)

        if (!subscription) return;

        if (subscription.subscriptionContractId === e.data.id) {
          const date = dayjs(e.data.deliverdate);
          const time = e.data.delivertime;
          const planId = e.data.deliverinterval;

          setIsProc(true);

          await setModifyPlanId(planId);
          await setDeliveryDate({
            ...deliveryDate!,
            change: dayjs(date),
          });

          try {
            if (planId === subscription.subscriptionLines[0].sellingPlanId) {
              await updateDeliveryDays(date, time, planId);
              // 両方のリクエストが成功した後の処理
              if (!isMessageSent) {
                await sendDeliveryDataToParent(date, time, planId);
                // subscriptionの更新
                updateSubscription();
              }
            } else {
              await updateSubscriptionLinePlan(planId, time);
              updateSubscription();
            }
          } catch (e) {
            console.error('エラーが発生しました:', e);
            // メッセージを送信
            window.parent.postMessage(
              {
                action: 'showErrorModal',
              },
              '*',
            );
          }

          setIsProc(false);
        }
      }

      lastEventData.current = e.data;
    },
    [subscription, planGroups],
  );

  if (!subscription) return <></>;

  let mindate = deliveryDate ? dayjs(deliveryDate!.min.toDate()) : undefined;
  let maxdate = deliveryDate && deliveryDate!.max ? dayjs(deliveryDate!.max.toDate()) : undefined;
  let betweenDate = maxdate && mindate ? maxdate.diff(mindate, 'days') : undefined;
  let selectdate = deliveryDate && deliveryDate!.change ? dayjs(deliveryDate!.change.toDate()) : undefined;
  // mindate から betweenDate までの配列を作成
  let betweenDateArray = [];
  if (betweenDate) {
    for (let i = 0; i < betweenDate; i++) {
      betweenDateArray.push(mindate!.add(i, 'day'));
    }
  }

  const disableWeek = () => {
    return [
      subscription!.shop.disableDeliveryDaysSun,
      subscription!.shop.disableDeliveryDaysMon,
      subscription!.shop.disableDeliveryDaysTue,
      subscription!.shop.disableDeliveryDaysWed,
      subscription!.shop.disableDeliveryDaysThr,
      subscription!.shop.disableDeliveryDaysFri,
      subscription!.shop.disableDeliveryDaysSat,
    ];
  };

  // モーダルID
  const modalid = subscription.subscriptionContractId.replace(/gid:\/\/shopify\/SubscriptionContract\//g, '');

  const nextDeliveryDate = (nextBillingDate: dayjs.Dayjs, deliveryDays: number): dayjs.Dayjs => {
    let nextDelivery = nextBillingDate.add(deliveryDays, 'day');

    if (!disableWeek().every((v) => v)) {
      const disableDays = disableWeek().slice(nextDelivery.day()).concat(disableWeek().slice(0, nextDelivery.day()));
      nextDelivery = nextDelivery.add(
        disableDays.findIndex((v) => !v),
        'days',
      );
    }

    return nextDelivery;
  };

  // お届け周期変更
  // -------------------------------------------
  async function updateSubscriptionLinePlan(planId: string, time: string) {
    if (!subscription) return;

    // 現在と同じプランの場合は処理をスキップ
    if (planId === subscription.subscriptionLines[0].sellingPlanId) return;

    const targetPlan = planGroups
      .map((planGroup) => planGroup.plans)
      .flat()
      .find((plan) => plan.planId === planId);

    if (!targetPlan) {
      throw new Error('Target plan not found');
    }

    const response = await axios.post(
      `${CUSTOMER_API_URL}?action=updateSubscriptionLinePlan`,
      {
        subscriptionId: subscription.subscriptionContractId,
        subscriptionLines: subscription.subscriptionLines.map((line) => ({
          planId: targetPlan.planId,
          planName: targetPlan.name,
          lineId: line.lineId,
          variantId: line.variantId,
          pricingPolicyAdjustmentType: targetPlan.pricingPolicyAdjustmentType,
          pricingPolicyAdjustmentValue: targetPlan.pricingPolicyAdjustmentValue,
        })),
        billingPolicyInterval: targetPlan.billingPolicyInterval,
        billingPolicyIntervalCount: targetPlan.billingPolicyIntervalCount,
      },
      {
        headers: { Authorization: setting.token },
      },
    );

    if (!response.data) throw new Error('Response data not found');

    console.log('updateSubscriptionLinePlan', response.data);

    await setSubscription({
      ...subscription!,
      ...response.data,
    });

    await setDeliveryDate({
      ...deliveryDate!,
      current: response.data.nextDeliveryDate,
      change: response.data.nextDeliveryDate,
      min: dayjs(response.data.nextBillingDate).add(subscription.shop.deliveryDays + 1, 'day'),
    });

    await updateDeliveryDays(dayjs(response.data.nextDeliveryDate), time, planId);

    sendDeliveryDataToParent(dayjs(response.data.nextDeliveryDate), time, planId);

    updateSubscription();

    setPageStatus({
      ...pageStatus,
      planModifyAction: false,
    });
  }

  // お届け日時を更新
  // -------------------------------------------
  async function updateDeliveryDays(date: dayjs.Dayjs, time: string, planId: string): Promise<void> {
    if (!subscription) return;

    const changeDate = date.add(1, 'day');
    const nextBilling = changeDate.subtract(subscription!.shop!.deliveryDays, 'day');

    let deliveryDays: number;
    if (subscription!.shop!.changeNextBillingDateWithDeliveryDate) {
      deliveryDays = deliveryDate!.noDay ? -1 : subscription!.shop!.deliveryDays;
    } else {
      deliveryDays = changeDate.diff(dayjs(subscription!.nextBillingDate), 'day');
    }

    const response = await axios.post(
      `${CUSTOMER_API_URL}?action=updateDeliveryDays`,
      {
        subscriptionId: id,
        deliveryDays: deliveryDays,
        nextBilling,
        deliveryTime: time,
        deliveryDate: deliveryDate ? deliveryDate.current.format('YYYY年M月D日') : '',
      },
      {
        headers: { Authorization: setting.token },
      },
    );

    console.log(response.data);

    await setSubscription({
      ...subscription!,
      ...response.data,
      nextBillingDate: new Date(response.data.nextBillingDate),
      disabled: false,
    });

    await setDeliveryDate({
      ...deliveryDate!,
      current: dayjs(response.data.nextBillingDate).add(subscription.deliveryDays + 1, 'day'),
      change: dayjs(response.data.nextBillingDate).add(subscription.deliveryDays + 1, 'day'),
      min: dayjs(response.data.nextBillingDate).add(subscription.deliveryDays + 1, 'day'),
    });

    await updateSubscription();
  }

  // 枠外にモーダル表示
  // -------------------------------------------
  const sendDeliveryDataToParent = async (date: any, time: any, planId: string) => {
    console.log('open complete modal', subscription, deliveryDate);

    const nextDeliveryDateText = deliveryDate
      ? deliveryDate.noDay
        ? '指定なし'
        : `${date.format('YYYY年M月D日')} ${
            subscription.shop.deliveryTimes.find((deliveryTime) => deliveryTime.tag === time)
              ? ` ${subscription.shop.deliveryTimes.find((deliveryTime) => deliveryTime.tag === time)!.title}`
              : ''
          }`
      : '';

    // メッセージを送信
    window.parent.postMessage(
      {
        action: 'showDeliverCompleteModal',
        nextDeliveryDate: nextDeliveryDateText,
        nextDeliveryCycle: getPlanModifyCycle(planId),
      },
      '*',
    );
    // メッセージ送信済みに設定
    setIsMessageSent(true);
  };

  // お届け周期変更のプルダウンリスト取得
  const getPlanModifyList = () => {
    if (!planGroups || planGroups.length <= 0 || !subscription) {
      return;
    }

    // 設定されている商品バリエーションを確認
    const planGroupsFilter = planGroups
      .filter((planGroup: PlanGroup) => {
        // 同一プランが含まれるか確認
        const plan = planGroup!.plans.find((plan) => {
          return (
            plan.billingPolicyInterval === subscription.billingPolicyInterval &&
            plan.billingPolicyIntervalCount === subscription.billingPolicyIntervalCount
          );
        });
        if (!plan) {
          return false;
        }

        // SubscriptionLineのsellingPlanIdがPlanGroupのPlanのいずれかに一致する
        return subscription.subscriptionLines.some((line) =>
          planGroup.plans.some((plan) => plan.planId === line.sellingPlanId),
        );
      })
      .map((planGroup: PlanGroup) => {
        return planGroup.plans;
      });

    let samePlans: Plan[] = [];
    if (planGroupsFilter.length <= 1) {
      samePlans = planGroupsFilter.flat();
    } else {
      // 全てのプランで、重複しているプランを抽出
      let plans = planGroupsFilter.flat();
      while (plans.length) {
        const target = plans.shift();
        const check = plans.filter((plan) => {
          return (
            plan.billingPolicyIntervalCount === target!.billingPolicyIntervalCount &&
            plan.billingPolicyInterval === target!.billingPolicyInterval
          );
        });

        if (check.length) {
          samePlans = samePlans.concat([target!]);
          plans = plans.filter(
            (plan) =>
              plan.billingPolicyIntervalCount !== target!.billingPolicyIntervalCount ||
              plan.billingPolicyInterval !== target!.billingPolicyInterval,
          );
        }
      }
    }

    return samePlans;
  };

  const modifyList = getPlanModifyList();
  let is_BOXPlan = false;

  const sellingPlanId = subscription && subscription.subscriptionLines[0].sellingPlanId;
  const matchedPlan = modifyList && modifyList.find((plan) => plan.planId === sellingPlanId);

  if (matchedPlan) {
    is_BOXPlan = /BOX/.test(matchedPlan.name);
  }

  const openChangeModals = async () => {
    // Micromodal.show(`modal-delivery-days-${modalid}`);

    // iframe枠外モーダル表示
    // -------------------------------------------
    const businessHolidays = subscription?.shop.businessHolidays.map((d) => dayjs(d.targetDate).format('YYYY-MM-DD'));
    let betweenDateArray = [];
    if (betweenDate) {
      for (let i = 0; i < betweenDate; i++) {
        const date = mindate!.add(i, 'day');
        if (date) {
          if (businessHolidays && businessHolidays.length > 0) {
            const isHoliday = businessHolidays.includes(date.format('YYYY-MM-DD'));
            if (!isHoliday) {
              betweenDateArray.push(date);
            }
          } else {
            betweenDateArray.push(date);
          }
        }
      }
    }

    // お届け予定日取得
    const betweenDates = betweenDateArray.map((v) => {
      let selected = v.format('YYYY-MM-DD') === selectdate?.format('YYYY-MM-DD') ? true : false;
      return {
        label: v.format('YYYY/MM/DD'),
        value: v.format('YYYY-MM-DD'),
        selected: selected,
      };
    });

    // お届け時間帯取得
    let deliveryTime = subscription.shop.deliveryTimes.map((deliveryTime) => {
      let selected = deliveryTime.tag === deliveryDate?.time ? true : false;
      return {
        label: deliveryTime.title,
        value: deliveryTime.tag,
        selected: selected,
      };
    });

    // お届けサイクル取得
    const deliveryInterval = modifyList!.map((plan) => {
      let selected: boolean = plan.planId === subscription.subscriptionLines[0].sellingPlanId ? true : false;
      let is_boxplan = plan.name.match(/BOX/) ? true : false;

      // BOXプランの場合は[BOX]を表示
      if ((is_BOXPlan && is_boxplan) || (!is_BOXPlan && !is_boxplan)) {
        return {
          label: plan.billingPolicyIntervalCount + billingPolicyIntervals[plan.billingPolicyInterval],
          value: plan.planId,
          selected: selected,
        };
      } else {
        return null;
      }
    });

    // Parentへ送信
    window.parent.postMessage(
      {
        action: 'showDeliverModal',
        betweenDateArray: betweenDates,
        deliveryTime: deliveryTime,
        deliveryInterval: deliveryInterval,
        subscriptionId: subscription.subscriptionContractId,
      },
      '*',
    );
  };

  const getPlanModifyCycle = (modifyplan: string) => {
    console.log({ modifyplan });

    if (!planGroups || !planGroups.length) {
      return '';
    }

    const plan = planGroups
      .map((planGroup) => {
        return planGroup.plans;
      })
      .flat()
      .find((plan) => plan.planId === modifyplan);

    console.log({ plan });

    if (!plan?.billingPolicyIntervalCount || !plan?.billingPolicyInterval) return '';
    return plan.billingPolicyIntervalCount + billingPolicyIntervals[plan.billingPolicyInterval];
  };

  return (
    <>
      <div className={SubscriptionListClass.OtherMenu}>
        <button className={SubscriptionListClass.subscriptionContractHeaderMenusButton} onClick={openChangeModals}>
          <img src="/image/button_changedate.svg" alt="お届け日の変更" />
        </button>
      </div>
    </>
  );
};

export default ChangeDeliverDate;
